/**
 * @Author: v_jppjpeng
 * @Date: 2022-09-20 11:24:55
 * @Explain: 工具类
 */

import { RoleCodeEnum } from '@/api/common/data.d';
import { API } from '@/config';
import { LanguageEnum, StoreKeyEnum } from '@/definition/common/data.d';
import { ContactEngagementOperatorTypeEnum } from '@/pages/segmentsManage/utils/typings/addOrEdit';
import { getLocalStorage } from '@/utils/global';
import type { ReactNode } from 'react';
import { history } from 'umi';
import { isArray, isFunction } from './dataJudgment';
/** 判断是否Array,是否有长度，并返回数据 */
export function getArrayValue(
  value: any[],
  returnValue?: unknown,
): any[] | any {
  return isArray(value) && value.length ? value : returnValue;
}

/** 获取首字 */
export function getFirstWord(value?: string, vacancyCharacter = '-'): string {
  try {
    return value ? value.substring(0, 1) : vacancyCharacter;
  } catch (e) {
    console.error('getFirstWord-----', e);
    return vacancyCharacter;
  }
}
/** 首字母大写 */
export function setInitialsCap(value: string): string {
  return value && value.replace(/^\S/, (s) => s.toUpperCase());
}

/** 去掉所有空格 */
export function trimAll(value: string): string {
  return value && value.replace(/\s/g, '');
}
/** 转字符串 */
export const toString = (value: unknown | any): string =>
  value ? value.toString() : '';

// 字符串指定位置插入字符串
export const insertStr = (source: string, start: number, newStr: string) => {
  return source.slice(0, start) + newStr + source.slice(start);
};

// 超出省略号
export function beyondEllipsis(
  value: string,
  maxLength: number,
  ellipsis?: string,
) {
  if (!ellipsis) ellipsis = '...';
  return value && value.length > maxLength
    ? value.substring(0, maxLength) + ellipsis
    : value;
}

/** join - 数组转字符串 */
export function onJoinData(value: any[], symbol = ','): string {
  let newValue = '';
  try {
    if (isArray(value)) {
      newValue = value.join(symbol);
    }
  } catch (e) {
    console.error('onJoinData-----', e);
  }
  return newValue;
}

/** split - 字符串转数组 */
export function onSplitData(
  value: string,
  symbol: string | RegExp = ',',
): any[] {
  let newValue: any[] = [];
  try {
    if (value && typeof value === 'string') {
      newValue = value.split(symbol);
    }
  } catch (e) {
    console.error('onSplitData-----', e);
  }
  return newValue;
}

// setTimeOut
export function setTimeOut(fn: () => void, delay: number = 500) {
  if (isFunction(fn)) {
    const timer = setTimeout(() => {
      clearTimeout(timer);
      fn();
    }, delay);
  }
}

// 升序 - 从小到大
export const sortAsc = (arr: any[], key: string) =>
  arr.sort((a, b) => a[key] - b[key]);
// 降序 - 从大到小
export const sortDesc = (arr: any[], key: string) =>
  arr.sort((a, b) => b[key] - a[key]);

/** 深拷贝 */
export const deepCopy = (source: any) => {
  if (!(typeof source === 'object' && source !== null)) return source; // 如果不是对象的话直接返回
  const target: any = Array.isArray(source) ? [] : {}; // 数组兼容
  for (const k in source) {
    if (source.hasOwnProperty(k)) {
      if (typeof source[k] === 'object') {
        target[k] = deepCopy(source[k]);
      } else {
        target[k] = source[k];
      }
    }
  }
  return target;
};

/** 数组对象 去重 */
export const arrayObjectUnique = (arr: any[], key: string) => {
  const res = new Map();
  return arr.filter((arr) => !res.has(arr[key]) && res.set(arr[key], 1));
};

/** 数组 去重 */
export const arrayUnique = (arr: any[]) => {
  return Array.from(new Set(arr));
};

/** 返回上一级 */
export const goBack = () => window.history.go(-1);
/** 查询链接上的参数 */
export const getLocationQuery = (name: string): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || '';
};

/** a标签下载文件 */
export const aDownloadFile = (url: any, downName?: string) => {
  const a = document.createElement('a'); // 创建a标签
  if (downName) {
    a.setAttribute('download', downName); // download属性
  }
  a.setAttribute('target', '_blank'); // 打开新窗口
  a.setAttribute('href', url); // href链接

  a.click(); // 自执行点击事件
};

/** 文件流下载 */
export const downloadBlob = (
  data: any,
  fileType: string,
  fileName?: string,
) => {
  const URL = window.URL || window.webkitURL;
  const blob = new Blob([data], { type: `${fileType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  aDownloadFile(url, fileName);
};

/** 下载文件 */
export const downloadFile = (url: any, downName?: string) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.responseType = 'blob';
  // eslint-disable-next-line func-names
  xhr.onload = function () {
    if (this.status === 200) {
      const blob = new Blob([this.response]);
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      if (downName) {
        a.download = downName; // download属性
      }
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    }
  };
  xhr.send();
};

/** 获取字符串body */
export function getHtmlBody(content: string) {
  const result = /<body[^>]*>([\s\S]*)<\/body>/.exec(content);
  if (result && result.length === 2) return result[1];
  return content;
}

/** 生成html */
export const getHtmlTemp = (val: string) => {
  let str = val;
  // 如果没有html且有body
  if (val.indexOf('<html') === -1 && val.indexOf('<body') !== -1) {
    str = `<html><head><meta charset="utf-8"></head>${val}</html>`;
  }
  // 如果没有html且没有body
  if (val.indexOf('<html') === -1 && val.indexOf('<body') === -1) {
    str = `<html><head><meta charset="utf-8"></head><body>${val}</body></html>`;
  }
  return str;
};

/** 获取html纯文本 */
export const getHtmlText = (htmlStr: string) => {
  const parser = new DOMParser();
  const doc = htmlStr
    ? parser.parseFromString(htmlStr, 'text/html')
    : undefined;
  const text = doc && doc.body.textContent;
  return text ? text?.replace(/\s+/g, '').trim() : '';
};

// 防抖
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timer!);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

// 节流
export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (!timer) {
      timer = setTimeout(() => {
        fn.apply(this, args);
        timer && clearTimeout(timer);
        timer = null;
      }, delay);
    }
  };
}

/**
 * 防抖函数
 * @param func 需要防抖执行的函数
 * @param wait 延迟执行的时间（毫秒）
 * @param immediate 是否立即执行第一次（true为立即执行，false为延迟执行）
 */
export function debounceNew<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const context = this;

    const later = () => {
      timer = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };

    const callNow = immediate && !timer;

    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(later, wait);

    if (callNow) {
      func.apply(context, args);
    }
  };
}

/**
 * 节流函数
 * @param func 需要节流执行的函数
 * @param wait 间隔执行的时间（毫秒）
 */
export function throttleNew<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let previous = 0;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const context = this;
    const now = Date.now();
    const remaining = wait - (now - previous);

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func.apply(context, args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now();
        timeout = null;
        func.apply(context, args);
      }, remaining);
    }
  };
}

interface fuzzySearchEurLangTypes {
  /** 搜索内容 */
  inputVal: string;
  /** 待筛选项 */
  opts: {
    value: number | string;
    text: string | React.ReactNode;
    [key: string]: any;
  }[];
}
// 输入内容于opts对比
const compareFuzzySearch = (val: string) => {
  const accents =
    '!¡?¿ÀÁÂÄÃĄÆàáâäãąæĆÇćçÈÉÊËĘèéêëęĞğÌÍÎÏİìíîïıŁłŃÑńñÒÓÔÖÕŒòóôöõœÙÚÛÜùúûüŚŞśşẞŻŹżź';
  const replacements =
    '!!??AAAAAAAaaaaaaaCCccEEEEEeeeeeGgIIIIIiiiiiLlNNnnOOOOOOooooooUUUUuuuuSSsssZZzz';
  let str = '';
  for (let i = 0; i < val.length; i++) {
    const char = val[i];
    const accentIndex = accents.indexOf(char);

    if (accentIndex !== -1) {
      str += replacements[accentIndex].toLowerCase();
    } else {
      str += char.toLowerCase();
    }
  }
  return str;
};

// 模糊匹配重音字符及西班牙语（倒挂问号、倒挂感叹号）
export const fuzzySearchEurLang = (params: fuzzySearchEurLangTypes) => {
  const { inputVal = '', opts = [] } = params;

  if (inputVal) {
    const result: any[] = [];
    for (let v = 0; v < opts.length; v++) {
      const item = opts[v];
      if (item.text && typeof item.text === 'string') {
        const itemSwitch = compareFuzzySearch(item.text);
        const inputValSwitch = compareFuzzySearch(inputVal);
        if (itemSwitch.includes(inputValSwitch)) {
          result.push(item);
        } else if (item.text.toLowerCase().includes(inputVal.toLowerCase())) {
          result.push(item);
        }
      }
    }
    return result;
  }
  return opts;
};
// 模糊匹配重音字符及西班牙语（倒挂问号、倒挂感叹号）
// 输入内容是否存在完全相同信息
export const getSameEurLang = (params: fuzzySearchEurLangTypes) => {
  const { inputVal = '', opts = [] } = params;

  let status: boolean = true;
  if (inputVal) {
    for (let v = 0; v < opts.length; v++) {
      const item = opts[v];
      if (item.text && typeof item.text === 'string') {
        const itemSwitch = compareFuzzySearch(item.text);
        const inputValSwitch = compareFuzzySearch(inputVal);
        if (
          itemSwitch === inputValSwitch ||
          item.text === inputVal.toLowerCase()
        ) {
          status = false;
        }
      }
    }
  }
  return status;
};

// 获取图片链接
export const getImgUrl = (path: string) => {
  const domain = `${API.staticResourceHost}/images`;
  return domain + path;
};

// 获取模板链接
export const getTemplateDownUrl = (path: string) => {
  const domain = `${API.staticResourceHost}/templateDownload`;
  return domain + path;
};

// 回到顶部
export const onBackTop = (className = 'smartlink-layout-main') => {
  document.getElementsByClassName(className)[0]?.scrollTo({
    top: 0, // 滚动到容器的顶部
    behavior: 'smooth', // 平滑滚动效果
  });
};
// 格式化详情数据 将【operatorType】统一设置成【none】
export const formatCampaignScope = (data: any = []) => {
  // 遍历每个对象
  data.forEach((item) => {
    // 遍历每个对象的 filters 数组
    if (Array.isArray(item.filters)) {
      item.filters.forEach((filter) => {
        if (filter.campaignScope.operatorType === 'least') {
          // 将 operatorType 改为 'least'
          filter.campaignScope.operatorType =
            ContactEngagementOperatorTypeEnum.NONE;
        }
      });
    }
  });

  return data;
};
// 设置 query 参数，不跳转页面
export const setQueryParams = (params: Record<string, any>) => {
  // 确保 history 对象存在
  if (!history || !history.location) {
    console.error('History object is not available');
    return;
  }

  const currentPath = history.location.pathname;
  const currentSearch = history.location.search;
  const currentQuery = new URLSearchParams(currentSearch);

  // 更新或添加参数
  Object.keys(params).forEach((key) => {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key] === ''
    ) {
      currentQuery.delete(key);
    } else {
      currentQuery.set(key, params[key]);
    }
  });

  const newSearch = currentQuery.toString();

  // 替换当前 URL
  history.replace({
    pathname: currentPath,
    search: newSearch ? `?${newSearch}` : '',
  });
};
/**
 * 英文状态下 使用 ", "分割且最后一个使用 or 链接（如果有多个的情况下）
 * strs 需要处理的字符串数组
 * excludeStrs 需要排除的字符 (提取出关键词) 例如【owner】
 * **/
export const formatStrArr = (
  strs: string[],
  excludeStrs?: string[] = [
    RoleCodeEnum.INTERNAL?.split?.('SmartLink_')?.[1]?.toLowerCase?.(),
  ].filter((item) => item),
) => {
  if (strs.length === 0) return '';
  strs =
    excludeStrs.length === 0
      ? strs
      : strs.filter((item) => !excludeStrs.includes(item));
  // 获取当前的语言
  const currentLang = getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE);
  if (currentLang === LanguageEnum.ZH) return strs.join('、');
  if (strs.length === 1) return strs[0];
  // 英文语种下 取出最后一项 删除原数组的最后一项 输入如下 ['test1','test2','test3'] 输出如下 test1,test1 or test3
  const lastStr = strs.pop();
  return `${strs.join(', ')} or ${lastStr}`;
};

interface optsTextType {
  list: {
    value: number | string;
    label: string | ReactNode;
  }[];
  value: number | string;
  valueKey?: string;
  textKey?: string;
}
// 获取枚举信息中指定字段 映射信息
export const getOptsText = (params: optsTextType) => {
  const { list, value, valueKey, textKey } = params;

  const vk: string = valueKey || 'value';
  const tk: string = textKey || 'label';

  const str =
    list.find(
      (i: { value: number | string; label: string | ReactNode }) =>
        String(i[vk]) === String(value),
    )?.[tk] || '';

  return str;
};
